/** eslint-disable @next/next/no-img-element */
import Image, { type ImageProps } from "next/image"
import Link from "next/link"
import { Container, Stack, Typography } from "@octopus-energy/coral/atoms"
import { token } from "@octopus-energy/coral-design/tokens"
import { type Maybe } from "@oefr/utils/types"
import { ConditionalWrapper } from "@/components/atoms"
import { BLUR_DATA_URI } from "../../constants/blurDataUri"
import { computeImageDimensions } from "../../utils/computeImageDimensions"
import { optimizeStoryblokImage } from "../../utils/optimizeStoryblokImage"
import { parseImageSize } from "../../utils/parseImageSize"
import { StoryblokPicture } from "./StoryblokPicture"
import { css } from "@octopus-energy/coral-design/css"

type StoryblokImageProps = {
    src?: Maybe<string>
    tabletSrc?: Maybe<string>
    mobileSrc?: Maybe<string>
    caption?: Maybe<string>
    href?: Maybe<string>
    target?: "_blank" | "_self"
    addLightModeBackground?: boolean
} & Omit<ImageProps, "src">

export function StoryblokImage({
    src,
    tabletSrc,
    mobileSrc,
    alt,
    href,
    target,
    width,
    height,
    caption,
    fill,
    onClick,
    addLightModeBackground,
    ...rest
}: StoryblokImageProps) {
    const isNotValidImg = !src || !alt

    if (isNotValidImg) {
        return (
            <p>
                <strong>
                    <code>Image</code>
                </strong>{" "}
                requires a <code>src</code> and <code>alt</code>
            </p>
        )
    }

    const isSvg = /(.svg)$/.test(src)
    const optimizedSrc = optimizeStoryblokImage(src)
    const imageDimensions = computeImageDimensions(
        optimizedSrc,
        parseImageSize(width),
        parseImageSize(height),
        fill
    )

    if (isSvg) {
        return (
            <ConditionalWrapper
                condition={!!href}
                Wrapper={({ children }) => (
                    <Link
                        href={href ?? ""}
                        target={target}
                    >
                        {children}
                    </Link>
                )}
            >
                <img
                    style={{
                        position: "relative",
                        display: "block",
                        width: width && typeof width === "number" ? `${width / 10}rem` : "none",
                        height: height && typeof height === "number" ? `${height / 10}rem` : "none",
                        margin: "auto",
                        cursor: onClick ? "pointer" : "default",
                    }}
                    src={src}
                    alt={alt}
                    onClick={onClick}
                />
            </ConditionalWrapper>
        )
    }

    let image = (
        <Image
            src={optimizedSrc}
            alt={alt}
            width={imageDimensions?.width}
            height={imageDimensions?.height}
            fill={fill}
            placeholder="blur"
            blurDataURL={BLUR_DATA_URI}
            onClick={onClick}
            style={{ cursor: onClick ? "pointer" : "auto" }}
            {...rest}
        />
    )

    // Art direction for mobile and/or tablet
    if (mobileSrc || tabletSrc) {
        image = (
            <StoryblokPicture
                src={src}
                tabletSrc={tabletSrc}
                mobileSrc={mobileSrc}
                alt={alt}
                width={width}
                height={height}
                onClick={onClick}
                style={{ cursor: onClick ? "pointer" : "auto" }}
                {...rest}
            />
        )
    }

    const imageContainerStyle = css(addLightModeBackground && {
        // adds a container around the image so content is visible in light mode even on a dark background
        _light: {
            borderRadius: token("borderRadius.base"),
            backgroundColor: 'white', // this doesn't require a token as we just need images to be visible in light mode on a dark background
        }
    });

    return (
        <ConditionalWrapper
            condition={!!href}
            Wrapper={({ children }) => (
                <Link
                    href={href ?? ""}
                    target={target}
                >
                    {children}
                </Link>
            )}
        >
            <Container
                component="figure"
                position="relative"
                margin="none"
            >
                <Container
                    position="relative"
                    height={fill ? parseImageSize(height) : "auto"}
                >
                    <div className={imageContainerStyle}>
                        <Stack
                            justifyContent="center"
                            gap="none"
                            fullHeight
                        >
                            {image}
                        </Stack>
                    </div>
                </Container>

                {caption && (
                    <Container
                        component="figcaption"
                        paddingY="sm"
                    >
                        <Typography
                            textStyle="caption"
                            textAlign="center"
                        >
                            {caption}
                        </Typography>
                    </Container>
                )}
            </Container>
        </ConditionalWrapper>
    )
}
